import React, { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { surface } from '../../theme';
import OpenCodeChatService from '../../services/OpenCodeChatService';
import ChatTranscript, { ChatImage } from './ChatTranscript';

const POLL_MS = 2000;
const ACTIVE_STATUSES = ['launching', 'starting', 'ready'];
const STATUS_CHIPS = {
  launching: { label: 'Starting', color: 'info' },
  starting: { label: 'Loading session', color: 'info' },
  ready: { label: 'Ready', color: 'success' },
  stopped: { label: 'Ended', color: 'default' },
  failed: { label: 'Failed', color: 'error' },
};

function errorMessage(err, fallback) {
  return err.response?.data?.error || fallback;
}

function versionLabel(version) {
  if (version === null || version === undefined) return 'a new session';
  return version === 0 ? 'the original session' : `version ${version}`;
}

function formatCost(cost) {
  return cost < 0.01 ? `$${cost.toFixed(4)}` : `$${cost.toFixed(2)}`;
}

function SessionsPanel({ sessions, onRestore, busy }) {
  return (
    <Paper sx={{ p: 2.5, borderRadius: 2, background: surface.glass, boxShadow: 2 }}>
      <Typography variant="h6" sx={{ mb: 0.5 }}>Saved sessions</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Idea runs and new chats start from the current version. Committing a chat adds a version.
      </Typography>
      {sessions.map((session) => (
        <Box key={session.version} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.75, borderTop: 1, borderColor: 'divider' }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {session.version === 0 ? 'Original' : `Version ${session.version}`}
              {session.current && <Chip size="small" color="primary" label="Current" sx={{ ml: 1, height: 20 }} />}
            </Typography>
            <Typography variant="caption" color="text.secondary" component="div">
              {session.note || (session.restoredFrom !== null ? `Restored from version ${session.restoredFrom}` : 'No note')}
            </Typography>
            <Typography variant="caption" color="text.secondary" component="div">
              {[session.createdBy, session.createdAt && new Date(session.createdAt).toLocaleString()].filter(Boolean).join(' · ')}
            </Typography>
          </Box>
          {!session.current && session.hasSession && (
            <Button size="small" onClick={() => onRestore(session)} disabled={busy}>Use</Button>
          )}
        </Box>
      ))}
    </Paper>
  );
}

function RecentChats({ chats, selectedId }) {
  if (!chats.length) return null;
  return (
    <Paper sx={{ p: 2.5, borderRadius: 2, background: surface.glass, boxShadow: 2 }}>
      <Typography variant="h6" sx={{ mb: 1 }}>Recent chats</Typography>
      {chats.map((chat) => {
        const status = STATUS_CHIPS[chat.status];
        return (
          <Box key={chat.id} sx={{ py: 0.75, borderTop: 1, borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
              <Link component={RouterLink} to={`?chat=${chat.id}`} variant="body2" sx={{ fontWeight: chat.id === selectedId ? 700 : 500 }}>
                {new Date(chat.createdAt).toLocaleString()}
              </Link>
              <Chip size="small" color={status.color} label={status.label} />
            </Box>
            <Typography variant="caption" color="text.secondary">
              {chat.startedBy} · {chat.messageCount} messages · from {versionLabel(chat.baseVersion)}
              {chat.committedVersion !== null && ` · committed as version ${chat.committedVersion}`}
            </Typography>
          </Box>
        );
      })}
    </Paper>
  );
}

function StartChat({ sessions, enabled, problem, onStarted }) {
  const current = sessions.find((session) => session.current);
  const [version, setVersion] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const choices = sessions.filter((session) => session.hasSession);

  useEffect(() => {
    if (current && version === '') setVersion(String(current.version));
  }, [current, version]);

  const start = async () => {
    try {
      setBusy(true);
      setError('');
      onStarted(await OpenCodeChatService.start(version === '' ? undefined : Number(version)));
    } catch (err) {
      setError(errorMessage(err, 'Could not start the chat.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 560, mx: 'auto', py: 6, textAlign: 'center' }}>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>Chat with OpenCode</Typography>
      <Typography color="text.secondary" sx={{ mb: 3 }}>
        OpenCode starts in the cloud and continues a saved session. Ask it for ideas and thumbnails, tell it what to
        change, and commit the chat when it gets them right. From then on, idea runs start from your chat.
      </Typography>
      {!enabled && <Alert severity="warning" sx={{ mb: 2, textAlign: 'left' }}>{problem}</Alert>}
      {choices.length > 1 && (
        <TextField
          select
          fullWidth
          size="small"
          label="Start from"
          value={version}
          onChange={(e) => setVersion(e.target.value)}
          sx={{ mb: 2, textAlign: 'left' }}
        >
          {choices.map((session) => (
            <MenuItem key={session.version} value={String(session.version)}>
              {session.version === 0 ? 'Original session' : `Version ${session.version}`}
              {session.current ? ' (current)' : ''}
              {session.note ? ` · ${session.note}` : ''}
            </MenuItem>
          ))}
        </TextField>
      )}
      {error && <Alert severity="error" sx={{ mb: 2, textAlign: 'left' }}>{error}</Alert>}
      <Button variant="contained" size="large" onClick={start} disabled={busy || !enabled}>
        {busy ? 'Starting…' : 'Start chat'}
      </Button>
      <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 1.5 }}>
        Loading a large session takes a few minutes.
      </Typography>
    </Box>
  );
}

function CommitDialog({ open, chat, onClose, onCommitted }) {
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const commit = async () => {
    try {
      setBusy(true);
      setError('');
      onCommitted(await OpenCodeChatService.commit(chat.id, note));
      setNote('');
    } catch (err) {
      setError(errorMessage(err, 'Could not commit the chat.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => !busy && onClose()} maxWidth="sm" fullWidth>
      <DialogTitle>Commit this chat</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          The whole conversation, including everything before this chat, is saved as the next session version.
          Daily and requested idea runs, and new chats, then start from it. You can switch back to an earlier
          version at any time.
        </Typography>
        <TextField
          autoFocus
          fullWidth
          margin="normal"
          label="What this version gets right"
          placeholder="Brighter thumbnails, more varied title openings"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          disabled={busy}
        />
        {error && <Alert severity="error">{error}</Alert>}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button variant="contained" onClick={commit} disabled={busy}>{busy ? 'Committing…' : 'Commit'}</Button>
      </DialogActions>
    </Dialog>
  );
}

// OpenCode's own web UI for a ready chat. The link signs the person in to the UI's host.
function OpenCodeFrame({ chat }) {
  const [url, setUrl] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (!chat.uiReady || url) return;
    OpenCodeChatService.uiLink(chat.id)
      .then((data) => setUrl(data.url))
      .catch((err) => setError(errorMessage(err, 'Could not open OpenCode.')));
  }, [chat.id, chat.uiReady, url]);

  if (error) return <Alert severity="error">{error}</Alert>;
  if (!url) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5, height: '100%' }}>
        <CircularProgress size={20} />
        <Typography color="text.secondary">Opening OpenCode…</Typography>
      </Box>
    );
  }
  return (
    <Box
      component="iframe"
      src={url}
      title="OpenCode"
      allow="clipboard-read; clipboard-write"
      sx={{ display: 'block', width: '100%', height: '100%', border: 0, borderRadius: '10px', bgcolor: '#141414' }}
    />
  );
}

// The chat itself: OpenCode's UI while it runs, the saved conversation once it ended.
function ChatMain({ chat }) {
  const active = ACTIVE_STATUSES.includes(chat.status);
  if (active && chat.uiReady) {
    return (
      <Box sx={{ height: { xs: '75vh', lg: 'calc(100vh - 190px)' }, minHeight: 560 }}>
        <OpenCodeFrame key={chat.id} chat={chat} />
      </Box>
    );
  }
  if (active) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5, color: 'text.secondary', py: 10 }}>
        <CircularProgress size={20} />
        <Typography>
          {chat.status === 'launching'
            ? 'Starting a container for OpenCode…'
            : `Loading ${versionLabel(chat.baseVersion)}… this takes a few minutes for a large session.`}
        </Typography>
      </Box>
    );
  }
  return (
    <Box sx={{ p: { xs: 1, md: 2 } }}>
      {chat.status === 'failed'
        ? <Alert severity="error" sx={{ mb: 2 }}>{chat.error}</Alert>
        : <Alert severity="info" sx={{ mb: 2 }}>{chat.endedReason || 'This chat has ended.'}</Alert>}
      {chat.messages.length
        ? <ChatTranscript chat={chat} />
        : <Typography color="text.secondary">No messages were sent in this chat.</Typography>}
    </Box>
  );
}

// Status, committing, ending the chat, and the images OpenCode saved.
function ChatControls({ chat, onChange, onEnded }) {
  const [error, setError] = useState('');
  const [committing, setCommitting] = useState(false);
  const [ending, setEnding] = useState(false);
  const [enlarged, setEnlarged] = useState(null);
  const active = ACTIVE_STATUSES.includes(chat.status);
  const ready = chat.status === 'ready';
  const commitPending = chat.commit?.status === 'pending';
  const status = STATUS_CHIPS[chat.status];

  const end = async () => {
    setEnding(false);
    try {
      setError('');
      onChange(await OpenCodeChatService.stop(chat.id));
      onEnded();
    } catch (err) {
      setError(errorMessage(err, 'Could not end the chat.'));
    }
  };

  return (
    <Paper sx={{ p: 2.5, borderRadius: 2, background: surface.glass, boxShadow: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
        <Typography variant="h6" sx={{ flex: 1 }}>This chat</Typography>
        <Chip size="small" color={status.color} label={chat.busy && ready ? 'Working' : status.label} />
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        From {versionLabel(chat.baseVersion)} · started by {chat.startedBy}
        {chat.cost > 0 && ` · ${formatCost(chat.cost)}`}
      </Typography>
      {active && (
        <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
          <Button
            variant="contained"
            onClick={() => setCommitting(true)}
            disabled={!ready || chat.busy || commitPending}
            sx={{ flex: 1 }}
          >
            {commitPending ? 'Committing…' : 'Commit'}
          </Button>
          <Button variant="outlined" color="error" onClick={() => setEnding(true)} disabled={commitPending}>
            End chat
          </Button>
        </Box>
      )}
      {active && chat.busy && (
        <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 1 }}>
          You can commit once OpenCode finishes its reply.
        </Typography>
      )}
      {chat.commit?.status === 'done' && (
        <Alert severity="success" sx={{ mb: 1.5 }}>
          Committed as version {chat.commit.version}. Idea runs and new chats now start from this conversation.
        </Alert>
      )}
      {chat.commit?.status === 'failed' && <Alert severity="error" sx={{ mb: 1.5 }}>Commit failed: {chat.commit.error}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError('')}>{error}</Alert>}
      {active && (
        <Typography variant="caption" color="text.secondary" component="div">
          Chats end after {chat.idleMinutes} minutes without activity, and what was not committed is lost.
        </Typography>
      )}

      {chat.images.length > 0 && (
        <>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 2, mb: 1 }}>Images ({chat.images.length})</Typography>
          <Stack spacing={1.5} sx={{ maxHeight: 520, overflowY: 'auto', pr: 0.5 }}>
            {[...chat.images].reverse().map((image) => (
              <ChatImage key={image.id} chatId={chat.id} image={image} onOpen={setEnlarged} width="100%" />
            ))}
          </Stack>
        </>
      )}

      <CommitDialog
        open={committing}
        chat={chat}
        onClose={() => setCommitting(false)}
        onCommitted={(next) => {
          setCommitting(false);
          onChange(next);
        }}
      />
      <Dialog open={ending} onClose={() => setEnding(false)} maxWidth="xs">
        <DialogTitle>End this chat?</DialogTitle>
        <DialogContent>
          <Typography>
            The container shuts down. {chat.commit?.status === 'done'
              ? 'Anything said after your last commit is lost.'
              : 'Nothing from this chat has been committed, so it will not change future ideas.'}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setEnding(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={end}>End chat</Button>
        </DialogActions>
      </Dialog>
      <Dialog open={Boolean(enlarged)} onClose={() => setEnlarged(null)} maxWidth="lg">
        {enlarged && <Box component="img" src={enlarged.url} alt={enlarged.path} sx={{ display: 'block', maxWidth: '100%' }} />}
      </Dialog>
    </Paper>
  );
}

/**
 * Chats with OpenCode running in the cloud. The chat to show is in ?chat=<id>; without
 * one, the person's running chat opens, or they can start a new one.
 */
export default function OpenCodeChatPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const chatId = searchParams.get('chat');
  const [overview, setOverview] = useState(null);
  const [chat, setChat] = useState(null);
  const [error, setError] = useState('');
  const [restoring, setRestoring] = useState(false);

  const loadOverview = useCallback(async () => {
    try {
      setOverview(await OpenCodeChatService.overview());
    } catch (err) {
      setError(errorMessage(err, 'Could not load OpenCode chats.'));
    }
  }, []);

  useEffect(() => { loadOverview(); }, [loadOverview]);

  // Without a chat in the address, open a running chat if there is one.
  useEffect(() => {
    if (chatId || !overview) return;
    const running = overview.chats.find((item) => ACTIVE_STATUSES.includes(item.status));
    if (running) setSearchParams({ chat: running.id }, { replace: true });
  }, [chatId, overview, setSearchParams]);

  useEffect(() => {
    setChat(null);
    if (!chatId) return undefined;
    let cancelled = false;
    let previous = null;
    let timer;
    const poll = async () => {
      try {
        const data = await OpenCodeChatService.get(chatId, previous?.revision);
        if (cancelled) return;
        if (!data.unchanged) {
          // A chat that just ended or committed changes the lists beside it.
          if (previous && (previous.status !== data.status || previous.commit?.status !== data.commit?.status)) {
            loadOverview();
          }
          previous = data;
          setChat(data);
        }
        if (data.unchanged || ACTIVE_STATUSES.includes(data.status)) timer = setTimeout(poll, POLL_MS);
      } catch (err) {
        if (!cancelled) setError(errorMessage(err, 'Could not load the chat.'));
      }
    };
    poll();
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [chatId, loadOverview]);

  const restore = async (session) => {
    try {
      setRestoring(true);
      await OpenCodeChatService.restore(session.version);
      await loadOverview();
    } catch (err) {
      setError(errorMessage(err, 'Could not switch the session.'));
    } finally {
      setRestoring(false);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <Button component={RouterLink} to="/video-ideas" startIcon={<ArrowBackIcon />} sx={{ color: 'white' }}>Ideas</Button>
        <Typography variant="h4" sx={{ fontWeight: 700, color: 'white', textShadow: '0 1px 2px rgba(0,0,0,0.2)', flex: 1 }}>
          OpenCode chat
        </Typography>
        {chatId && (
          <Button variant="contained" color="inherit" onClick={() => setSearchParams({})} sx={{ color: 'primary.main' }}>
            New chat
          </Button>
        )}
      </Box>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: 'minmax(0, 1fr) 340px' }, gap: 2, alignItems: 'start' }}>
        <Paper sx={{ p: chatId ? 1 : { xs: 2, md: 3 }, borderRadius: 2, background: surface.glass, boxShadow: 2 }}>
          {!overview ? (
            <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>
          ) : chatId ? (
            chat ? (
              <ChatMain chat={chat} />
            ) : (
              <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>
            )
          ) : (
            <StartChat
              sessions={overview.sessions}
              enabled={overview.enabled}
              problem={overview.problem}
              onStarted={(started) => {
                loadOverview();
                setSearchParams({ chat: started.id });
              }}
            />
          )}
        </Paper>
        {overview && (
          <Stack spacing={2}>
            {chatId && chat && <ChatControls chat={chat} onChange={setChat} onEnded={loadOverview} />}
            <SessionsPanel sessions={overview.sessions} onRestore={restore} busy={restoring} />
            <RecentChats chats={overview.chats} selectedId={chatId} />
          </Stack>
        )}
      </Box>
    </Box>
  );
}
